import React from 'react';
import { DisplayField } from 'signer-app/signature-request';
import {
  isMultilineField,
  useSignerTextInput,
} from 'signer-app/signer-signature-document/text-field-hooks';
import { TextField } from 'signer-app/types/editor-types';
import { FieldUpdate } from 'signer-app/signer-signature-document/types';

export function getValidTextFieldInput(field: TextField, input: string) {
  if (input.trim().length === 0) {
    return '';
  }

  // For single-line fields, handle newlines instead of rejecting the input
  if (!isMultilineField(field) && input.indexOf('\n') >= 0) {
    return input.replace(/[\n\r]/g, '');
  }

  return input;
}

type Props = {
  fieldData: TextField;
  isActive: boolean;
  updateField: (updates: FieldUpdate) => void;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onTouchEnd?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  getInstantValidationErrorHack?: (id: string) => void;
  isOverlay: boolean;
  documentPreview: boolean;
};

type RefWithTextRef = {
  textRef: React.RefObject<HTMLTextAreaElement>;
};

function TextFieldComponent(
  {
    updateField,
    onChange,
    fieldData,
    getInstantValidationErrorHack,
    isOverlay,
    documentPreview,
    ...props
  }: Props,
  r: React.ForwardedRef<RefWithTextRef>,
) {
  const hidden = React.useRef<RefWithTextRef>();
  const ref = r as React.MutableRefObject<RefWithTextRef>;

  let style: React.CSSProperties = {
    whiteSpace: isMultilineField(fieldData) ? 'pre-wrap' : 'pre',
    wordBreak: 'break-word',
    wordSpacing: '0em !important',
    letterSpacing: '0em',
  };

  let locationName: 'review' | 'preview' | 'signer' | 'overlay' = 'signer';
  if (isOverlay) {
    locationName = 'overlay';
  } else if (documentPreview) {
    locationName = 'preview';
  }

  useSignerTextInput(
    fieldData,
    ref.current?.textRef,
    hidden.current?.textRef,
    updateField,
    locationName,
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const validInput = getValidTextFieldInput(fieldData, e.target.value);

    // Ignore newlines on non-multiline fields
    if (validInput === null) {
      return;
    }

    e.target.value = validInput;

    onChange(e);
  };

  // adjust line hight to match up with what the backend uses
  style = {
    ...style,
    lineHeight: 1.2,
  };

  const value = fieldData.value || '';

  if (isOverlay && value === '') {
    return null;
  }

  return (
    <React.Fragment>
      <DisplayField
        {...props}
        placeholder={fieldData.placeholder || ''}
        fieldData={
          !props.isActive && fieldData.masked
            ? {
                ...fieldData,
                value: value.replace(/./gi, '*'),
              }
            : fieldData
        }
        // @ts-expect-error I know this code works, but the types aren't lining
        // up on the refs
        ref={ref}
        style={{
          ...style,
        }}
        onKeyDown={undefined}
        onChange={(e: React.ChangeEvent<Element>) =>
          handleChange(e as React.ChangeEvent<HTMLTextAreaElement>)
        }
      />
      <div
        style={{
          position: 'absolute',
          top: '100%',
          height: '100%',
          width: '100%',
          visibility: 'hidden',
        }}
      >
        <DisplayField
          {...props}
          fieldData={fieldData}
          style={{
            // The whole pemise of this componet is tha it renders exactly the
            // same componnt with one visible and one invisible.
            ...style,
            // minHeight and height need to be reset, because the amount of
            // space the text can occupy is measured by looking at the
            // `.scrollHeight`.
            minHeight: 0,
            height: 0,
            width: '100%',
            transform: 'none',
            // Please do not make intentional differences between the on-screen
            // and off-screen components. Any difference causes the measurements
            // to be invalid.
          }}
          placeholder={
            // do not apply placeholders here. It forces the field to be at
            // least the size of the placeholder, so it's not able to shrink
            // properly.
            ''
          }
          data-qa-ref={undefined}
          // @ts-expect-error I know this code works, but the types aren't
          // lining up on the refs
          ref={hidden}
        />
      </div>
    </React.Fragment>
  );
}

export default React.forwardRef(TextFieldComponent);
